import { RECREATE_ON_PARAMS_CHANGE } from './utils/param-aware-route-reuse.strategy';
import { Routes } from '@angular/router';
import { organizationResolver } from './resolvers/organization.resolver';
import { loggedInGuard } from './guards/logged-in.guard';
import { guestGuard } from './guards/guest.guard';
import { approvedGuard } from './guards/approved.guard';
import { superAdminGuard } from './guards/super-admin.guard';
import { organizationGuard } from './guards/organization.guard';
import { organizationSourceDataResolver } from './resolvers/organization-source-data.resolver';
import { organizationProvidersResolver } from './resolvers/organization-providers.resolver';
import { organizationProviderResolver } from './resolvers/organization-provider.resolver';

export const routes: Routes = [
  // Auth routes (accessible only when NOT logged in)
  {
    path: 'auth/login/with-code/:code',
    redirectTo: ({ params }): string => {
      if (params['code']) {
        return `/auth/login?login_code=${params['code']}`;
      }
      return '/auth/login';
    }
  },
  {
    path: 'auth/registration/:invitationToken',
    redirectTo: ({ params }): string => {
      if (params['invitationToken']) {
        return `/auth/register?invitation_token=${params['invitationToken']}`;
      }
      return '/auth/register';
    }
  },
  {
    path: 'auth/login',
    loadComponent: () => import('./pages/auth/login/login-page.component').then(m => m.LoginPageComponent),
    canActivate: [ guestGuard ],
  },
  {
    path: 'auth/register',
    loadComponent: () => import('./pages/auth/register/register-page.component').then(m => m.RegisterPageComponent),
    canActivate: [ guestGuard ],
  },

  // Status pages (logged in but not necessarily approved)
  {
    path: 'pending-approval',
    loadComponent: () => import('./pages/pending-approval/pending-approval.component').then(m => m.PendingApprovalComponent),
    canActivate: [ loggedInGuard ],
  },
  {
    path: 'registration-rejected',
    loadComponent: () => import('./pages/registration-rejected/registration-rejected.component').then(m => m.RegistrationRejectedComponent),
    canActivate: [ loggedInGuard ],
  },

  // Organization onboarding / selection.
  // Deliberately NOT behind organizationGuard — these are how a user without an
  // organization gets one. Gating them would be a dead end.
  {
    path: 'onboarding',
    loadComponent: () => import('./pages/onboarding/onboarding.component').then(m => m.OnboardingComponent),
    canActivate: [ loggedInGuard, approvedGuard ],
  },
  {
    path: 'select-organization',
    loadComponent: () => import('./pages/select-organization/select-organization.component').then(m => m.SelectOrganizationComponent),
    canActivate: [ loggedInGuard, approvedGuard ],
  },

  // Protected routes (require login)

  // `/` is the single landing-resolution point, not a page. It forwards to the
  // active organization's config, the picker, or onboarding. No organizationGuard —
  // it would pre-empt the resolution it is supposed to follow.
  {
    path: '',
    loadComponent: () => import('./pages/landing/landing.component').then(m => m.LandingComponent),
    canActivate: [ loggedInGuard, approvedGuard ],
  },

  // The marketing/discovery page formerly served at `/`.
  {
    path: 'discover',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
  },

  {
    path: 'search',
    loadComponent: () => import('./pages/search/search.component').then(m => m.SearchComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
  },
  {
    path: 'search/:searchType',
    loadComponent: () => import('./pages/search/search.component').then(m => m.SearchComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
  },

  {
    path: 'sponsor/:organizationId',
    data: { [RECREATE_ON_PARAMS_CHANGE]: true },
    loadComponent: () => import('./pages/profile-advertiser/profile-advertiser.component').then(m => m.ProfileAdvertiserComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
    resolve: {
      organization: organizationResolver,
      organizationSourceData: organizationSourceDataResolver,
      organizationProviders: organizationProvidersResolver,
    },
    children: [
      {
        path: '',
        redirectTo: 'summary',
        pathMatch: 'full'
      },
      {
        path: 'summary',
        loadComponent: () => import('./pages/profile-advertiser/profile-advertiser-summary/profile-advertiser-summary.component').then(m => m.ProfileAdvertiserSummaryComponent)
      },
    ]
  },

  {
    path: 'profile/:organizationId',
    data: { [RECREATE_ON_PARAMS_CHANGE]: true },
    loadComponent: () => import('./pages/profile-network/profile-network.component').then(m => m.ProfileNetworkComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
    resolve: {
      organization: organizationResolver,
      organizationSourceData: organizationSourceDataResolver,
      organizationProviders: organizationProvidersResolver,
    },
    children: [
      {
        path: '',
        redirectTo: 'summary',
        pathMatch: 'full'
      },
      {
        path: 'summary',
        loadComponent: () => import('./pages/profile-network/profile-network-summary/profile-network-summary.component').then(m => m.ProfileNetworkSummaryComponent)
      },
      {
        path: 'reports',
        loadComponent: () => import('./pages/profile-network/profile-network-reports/profile-network-reports.component').then(m => m.ProfileNetworkReportsComponent),
        children: [
          {
            path: ':organizationProviderId',
            loadComponent: () => import('./pages/profile-network/profile-network-reports/organization-provider-report/organization-provider-report.component').then(m => m.OrganizationProviderReportComponent),
            resolve: {
              organizationProvider: organizationProviderResolver
            }
          },
          {
            path: ':organizationProviderId/:providerAlias',
            loadComponent: () => import('./pages/profile-network/profile-network-reports/organization-provider-report/organization-provider-report.component').then(m => m.OrganizationProviderReportComponent),
            resolve: {
              organizationProvider: organizationProviderResolver
            }
          },
        ]
      },
      {
        path: 'audiences',
        loadComponent: () => import('./pages/profile-network/profile-network-audiences/profile-network-audiences.component').then(m => m.ProfileNetworkAudiencesComponent),
        children: [
          {
            path: ':organizationProviderId',
            loadComponent: () => import('./pages/profile-network/profile-network-audiences/organization-provider-audience/organization-provider-audience.component').then(m => m.OrganizationProviderAudienceComponent),
            resolve: {
              organizationProvider: organizationProviderResolver
            }
          },
          {
            path: ':organizationProviderId/:providerAlias',
            loadComponent: () => import('./pages/profile-network/profile-network-audiences/organization-provider-audience/organization-provider-audience.component').then(m => m.OrganizationProviderAudienceComponent),
            resolve: {
              organizationProvider: organizationProviderResolver
            }
          },
        ]
      },
      {
        path: 'matching',
        loadComponent: () => import('./pages/profile-network/profile-network-matching/profile-network-matching.component').then(m => m.ProfileNetworkMatchingComponent),
      },
      {
        path: 'events',
        loadComponent: () => import('./pages/profile-network/profile-network-events/profile-network-events.component').then(m => m.ProfileNetworkEventsComponent),
      },
      {
        path: 'sponsorship',
        loadComponent: () => import('./pages/profile-network/profile-network-sponsorship/profile-network-sponsorship.component').then(m => m.ProfileNetworkSponsorshipComponent),
      },
    ]
  },

  {
    path: 'account',
    loadComponent: () => import('./pages/account/account.component').then(m => m.AccountComponent),
    canActivate: [ loggedInGuard ],
  },

  {
    path: 'connect-claude',
    loadComponent: () => import('./pages/connect-claude/connect-claude.component').then(m => m.ConnectClaudeComponent),
    canActivate: [ loggedInGuard ],
  },

  {
    path: 'config/:organizationId',
    loadComponent: () => import('./pages/config/config.component').then(m => m.ConfigComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
    resolve: {
      organization: organizationResolver,
      organizationProviders: organizationProvidersResolver
    },
    children: [
      {
        path: '',
        redirectTo: 'analytics',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/config/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/config/settings/settings.component').then(m => m.SettingsComponent)
      },
      {
        path: 'brochures',
        loadComponent: () => import('./pages/config/brochure-list/brochure-list.component').then(m => m.BrochureListComponent)
      },
      {
        path: 'brochure/:brochureId',
        loadComponent: () => import('./pages/config/brochure-details/brochure-details.component').then(m => m.BrochureDetailsComponent)
      },
      {
        path: 'brochure',
        loadComponent: () => import('./pages/config/brochure-details/brochure-details.component').then(m => m.BrochureDetailsComponent)
      },
      {
        path: 'events',
        loadComponent: () => import('./pages/config/event-list/event-list.component').then(m => m.EventListComponent)
      },
      {
        path: 'event/:eventId',
        loadComponent: () => import('./pages/config/event-details/event-details.component').then(m => m.EventDetailsComponent)
      },
      {
        path: 'event',
        loadComponent: () => import('./pages/config/event-details/event-details.component').then(m => m.EventDetailsComponent)
      },
      {
        path: 'providers',
        loadComponent: () => import('./pages/config/provider-list/provider-list.component').then(m => m.ProviderListComponent)
      },
      {
        path: 'provider/:organizationProviderId',
        loadComponent: () => import('./pages/config/provider-details/provider-details.component').then(m => m.ProviderDetailsComponent)
      },
      {
        path: 'provider',
        loadComponent: () => import('./pages/config/provider-details/provider-details.component').then(m => m.ProviderDetailsComponent)
      },
      {
        path: 'channels',
        loadComponent: () => import('./pages/config/channel-list/channel-list.component').then(m => m.ChannelListComponent)
      },
      {
        path: 'channel/:channelId',
        loadComponent: () => import('./pages/config/channel-details/channel-details.component').then(m => m.ChannelDetailsComponent)
      },
      {
        path: 'channel',
        loadComponent: () => import('./pages/config/channel-details/channel-details.component').then(m => m.ChannelDetailsComponent)
      },
      {
        path: 'offers',
        loadComponent: () => import('./pages/config/offer-list/offer-list.component').then(m => m.OfferListComponent)
      },
      {
        path: 'offer/:offerId',
        loadComponent: () => import('./pages/config/offer-details/offer-details.component').then(m => m.OfferDetailsComponent)
      },
      {
        path: 'offer',
        loadComponent: () => import('./pages/config/offer-details/offer-details.component').then(m => m.OfferDetailsComponent)
      },
      {
        path: 'calculator',
        loadComponent: () => import('./pages/config/commercial-calculator/commercial-calculator.component').then(m => m.CommercialCalculatorComponent),
        resolve: {
          organizationSourceData: organizationSourceDataResolver
        }
      },
      {
        path: 'offer-groups',
        loadComponent: () => import('./pages/config/offer-group-list/offer-group-list.component').then(m => m.OfferGroupListComponent)
      },
      {
        path: 'offer-group/:offerGroupId',
        loadComponent: () => import('./pages/config/offer-group-details/offer-group-details.component').then(m => m.OfferGroupDetailsComponent)
      },
      {
        path: 'offer-group',
        loadComponent: () => import('./pages/config/offer-group-details/offer-group-details.component').then(m => m.OfferGroupDetailsComponent)
      },
      {
        path: 'campaigns',
        loadComponent: () => import('./pages/config/campaign-list/campaign-list.component').then(m => m.CampaignListComponent)
      },
      {
        path: 'campaign/:campaignId',
        loadComponent: () => import('./pages/config/campaign-details/campaign-details.component').then(m => m.CampaignDetailsComponent)
      },
      {
        path: 'campaign',
        loadComponent: () => import('./pages/config/campaign-details/campaign-details.component').then(m => m.CampaignDetailsComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/config/user-list/user-list.component').then(m => m.UserListComponent)
      },
      {
        path: 'users/:userId',
        loadComponent: () => import('./pages/config/user-details/user-details.component').then(m => m.UserDetailsComponent)
      },
      {
        path: 'blacklist',
        loadComponent: () => import('./pages/config/cie-blacklist/cie-blacklist.component').then(m => m.CieBlacklistComponent)
      },
      {
        path: 'exclusivity',
        loadComponent: () => import('./pages/config/cie-exclusivity/cie-exclusivity.component').then(m => m.CieExclusivityComponent)
      },
      {
        path: 'prospects',
        loadComponent: () => import('./pages/config/cie-prospects-list/cie-prospects-list.component').then(m => m.CieProspectsListComponent)
      },
      {
        path: 'prospect/:prospectOrganizationProviderId',
        loadComponent: () => import('./pages/config/cie-prospect-detail/cie-prospect-detail.component').then(m => m.CieProspectDetailComponent)
      },
      {
        path: 'prospect/:prospectOrganizationProviderId/contacts',
        loadComponent: () => import('./pages/config/cie-contacts/cie-contacts.component').then(m => m.CieContactsComponent)
      },
      {
        path: 'analytics',
        loadComponent: () => import('./pages/config/cie-analytics-shell/cie-analytics-shell.component').then(m => m.CieAnalyticsShellComponent),
        children: [
          {
            path: ':organizationProviderId',
            resolve: {
              organizationProvider: organizationProviderResolver
            },
            children: [
              {
                path: 'matching',
                loadComponent: () => import('./pages/config/cie-match-list/cie-match-list.component').then(m => m.CieMatchListComponent)
              },
              {
                path: 'brand-affinity',
                loadComponent: () => import('./pages/config/cie-brand-matching/cie-brand-matching.component').then(m => m.CieBrandMatchingComponent)
              },
              {
                path: 'audience',
                loadComponent: () => import('./pages/config/cie-audience/cie-audience.component').then(m => m.CieAudienceComponent)
              },
              {
                path: 'segments',
                loadComponent: () => import('./pages/config/cie-segments/cie-segments.component').then(m => m.CieSegmentsComponent)
              },
              {
                path: '',
                redirectTo: 'matching',
                pathMatch: 'full'
              },
            ]
          },
        ]
      },
    ]
  },

  {
    path: 'admin',
    loadComponent: () => import('./pages/admin/admin.component').then(m => m.AdminComponent),
    canActivate: [ loggedInGuard, approvedGuard, superAdminGuard ],
    children: [
      {
        path: '',
        redirectTo: 'organizations',
        pathMatch: 'full'
      },
      {
        path: 'organizations',
        loadComponent: () => import('./pages/admin/organization-list/organization-list.component').then(m => m.AdminOrganizationListComponent)
      },
      {
        path: 'organizations/import',
        loadComponent: () => import('./pages/admin/organization-import/organization-import.component').then(m => m.AdminOrganizationImportComponent)
      },
      {
        path: 'organization/:organizationId',
        loadComponent: () => import('./pages/admin/organization-details/organization-details.component').then(m => m.AdminOrganizationDetailsComponent)
      },
      {
        path: 'organization-offer-lists',
        loadComponent: () => import('./pages/admin/organization-offer-list-list/organization-offer-list-list.component').then(m => m.AdminOrganizationOfferListListComponent)
      },
      {
        path: 'organization-offer-list',
        loadComponent: () => import('./pages/admin/organization-offer-list-details/organization-offer-list-details.component').then(m => m.AdminOrganizationOfferListDetailsComponent)
      },
      {
        path: 'organization-offer-list/:organizationOfferListId',
        loadComponent: () => import('./pages/admin/organization-offer-list-details/organization-offer-list-details.component').then(m => m.AdminOrganizationOfferListDetailsComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/admin/user-list/user-list.component').then(m => m.AdminUserListComponent)
      },
      {
        path: 'user/:userId',
        loadComponent: () => import('./pages/admin/user-details/user-details.component').then(m => m.AdminUserDetailsComponent)
      },
      {
        path: 'invitations',
        loadComponent: () => import('./pages/admin/invitation-list/invitation-list.component').then(m => m.AdminInvitationListComponent)
      },
      {
        path: 'approvals',
        loadComponent: () => import('./pages/admin/approval-list/approval-list.component').then(m => m.AdminApprovalListComponent)
      },
      {
        path: 'categories',
        loadComponent: () => import('./pages/admin/category-list/category-list.component').then(m => m.AdminCategoryListComponent)
      },
      {
        path: 'category/:categoryId',
        loadComponent: () => import('./pages/admin/category-details/category-details.component').then(m => m.AdminCategoryDetailsComponent)
      },
      {
        path: 'cie-category-reports',
        loadComponent: () => import('./pages/admin/cie-category-report-list/cie-category-report-list.component').then(m => m.AdminCieCategoryReportListComponent)
      },
      {
        path: 'terms-of-services',
        loadComponent: () => import('./pages/admin/terms-of-service-list/terms-of-service-list.component').then(m => m.AdminTermsOfServiceListComponent)
      },
      {
        path: 'terms-of-service',
        loadComponent: () => import('./pages/admin/terms-of-service-details/terms-of-service-details.component').then(m => m.AdminTermsOfServiceDetailsComponent)
      },
      {
        path: 'terms-of-service/:termsOfServiceId',
        loadComponent: () => import('./pages/admin/terms-of-service-details/terms-of-service-details.component').then(m => m.AdminTermsOfServiceDetailsComponent)
      },
    ]
  },
  {
    path: 'assistant',
    loadComponent: () => import('./pages/assistant/assistant.component').then(m => m.AssistantComponent),
    canActivate: [ loggedInGuard, approvedGuard, organizationGuard ],
  },
];
